import React, { useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { IAppContext, AppContext } from 'appState/appContext';
import { IItemForGetShortInfoWithStatus, UserRoles } from 'types';
import { useRoleAccess } from 'hooks/useRoleAccess';
import MultipleSelect from './components/MultipleSelect';
import SelectedItem from 'components/SelectedItem/SelectedItem';
import style from './RestaurantsAvailableForRepetitionSelect.module.scss';

interface IRestaurantsAvailableForRepetitionSelect {
	setIDsOfRestaurantsAvailableForRepetition: (value: string[]) => void;
	IDSelectedCustomer: Nullable<string>;
	fetchedRestaurants: IItemForGetShortInfoWithStatus[];
	IDsSelectedRestaurant: string[];
	IDsOfRestaurantsAvailableForRepetition: string[];
	initialIDsOfRestaurantsAvailableForRepetition: string[];
}

const RestaurantsAvailableForRepetitionSelect: React.FC<IRestaurantsAvailableForRepetitionSelect> = ({
	setIDsOfRestaurantsAvailableForRepetition,
	IDSelectedCustomer,
	fetchedRestaurants,
	IDsSelectedRestaurant,
	IDsOfRestaurantsAvailableForRepetition,
	initialIDsOfRestaurantsAvailableForRepetition,
}) => {
	const { loggedUser } = useContext<IAppContext>(AppContext);
	const hasAccess = useRoleAccess([
		UserRoles.SUPER_USER,
		UserRoles.CLIENT_MANAGER,
		UserRoles.GROUP_MANAGER,
		UserRoles.TECHNICAL,
	]);
	const [isFirstRender, setIsFirstRender] = useState<boolean>(true);

	const restaurants = useMemo(() => {
		if (IDsSelectedRestaurant.length && fetchedRestaurants.length) {
			const filteredRestaurants = fetchedRestaurants.filter((el) => IDsSelectedRestaurant.includes(el.id));
			return filteredRestaurants;
		}
		return [];
	}, [fetchedRestaurants, IDsSelectedRestaurant]);

	const handleRemove = useCallback(
		(id: string) => {
			const updatedSelectedItems = IDsOfRestaurantsAvailableForRepetition.filter((el) => el !== id);
			setIDsOfRestaurantsAvailableForRepetition(updatedSelectedItems);
		},
		[IDsOfRestaurantsAvailableForRepetition, setIDsOfRestaurantsAvailableForRepetition],
	);

	useEffect(() => {
		if (isFirstRender && fetchedRestaurants.length) {
			setIDsOfRestaurantsAvailableForRepetition(initialIDsOfRestaurantsAvailableForRepetition);
			setIsFirstRender(false);
		}
	}, [fetchedRestaurants]);

	const restaurantsList = useMemo(() => {
		return IDsOfRestaurantsAvailableForRepetition.map((el) => {
			const label = fetchedRestaurants.find((item) => item.id === el)?.name || '';
			const accessibleRestaurantIds = loggedUser.restaurants?.map((el) => el.id);
			return (
				<SelectedItem
					key={el}
					label={label}
					onDelete={!hasAccess && !accessibleRestaurantIds?.includes(el) ? undefined : () => handleRemove(el)}
				/>
			);
		});
	}, [IDsOfRestaurantsAvailableForRepetition, fetchedRestaurants, loggedUser, hasAccess, handleRemove]);

	return (
		<div className={style.container} data-test="RestaurantsAvailableForRepetitionSelect">
			<div className={style.field}>
				<MultipleSelect
					data={restaurants || []}
					setIDsOfRestaurantsAvailableForRepetition={setIDsOfRestaurantsAvailableForRepetition}
					IDSelectedCustomer={IDSelectedCustomer}
					IDsOfRestaurantsAvailableForRepetition={IDsOfRestaurantsAvailableForRepetition}
					IDsSelectedRestaurant={IDsSelectedRestaurant}
					initialIDsOfRestaurantsAvailableForRepetition={initialIDsOfRestaurantsAvailableForRepetition}
				/>
			</div>
			<div className={style.groupsList}>{restaurantsList}</div>
		</div>
	);
};

export default React.memo(RestaurantsAvailableForRepetitionSelect);
